import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleNotch, Warning } from '@phosphor-icons/react'
import { orgApi } from '@/api/organizations'
import type { OrganizationDetail } from '@/api/organizations'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface OrgDangerZoneProps {
  org: OrganizationDetail
  onOrgUpdated?: () => void
}

export function OrgDangerZone({ org, onOrgUpdated }: OrgDangerZoneProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [showConfirm, setShowConfirm] = useState(false)

  const toggleMutation = useMutation({
    mutationFn: () =>
      orgApi.update(org.id, { is_active: !org.is_active }),
    onSuccess: () => {
      const action = org.is_active ? 'deactivated' : 'activated'
      toast({
        title: `Organization ${action}`,
        description: `${org.name} has been ${action}.`,
      })
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'detail', org.id],
      })
      queryClient.invalidateQueries({
        queryKey: ['organizations', 'list'],
      })
      onOrgUpdated?.()
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to ${org.is_active ? 'deactivate' : 'activate'} organization`,
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  function handleConfirm() {
    toggleMutation.mutate()
    setShowConfirm(false)
  }

  return (
    <>
      <Card className="border-destructive/50 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-destructive">
            <Warning className="h-5 w-5" weight="fill" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            {org.is_active
              ? 'Deactivating this organization will prevent all users from accessing the platform.'
              : 'This organization is currently inactive. Activating it will restore user access.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant={org.is_active ? 'destructive' : 'default'}
            onClick={() => setShowConfirm(true)}
            disabled={toggleMutation.isPending}
          >
            {toggleMutation.isPending && (
              <CircleNotch className="h-4 w-4 animate-spin" />
            )}
            {org.is_active ? 'Deactivate Organization' : 'Activate Organization'}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {org.is_active ? 'Deactivate' : 'Activate'} {org.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {org.is_active
                ? `Are you sure you want to deactivate ${org.name}? All ${org.user_count} users will lose access immediately.`
                : `Are you sure you want to activate ${org.name}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={org.is_active ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              {org.is_active ? 'Deactivate' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
